/**
 * <OnboardingCallout/> — inline onboarding banner for the Dashboard.
 *
 * Replaces the previous floating SETUP pill. Auto-hides when there's no
 * eligible onboarding state or when the wizard is complete. Designed to sit
 * at the top of the Dashboard for Admin / Supervisor / Agent.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useOnboarding } from "@/context/OnboardingContext";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, Circle, SkipForward, ArrowRight } from "@phosphor-icons/react";

export default function OnboardingCallout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state, steps, loading, isEligible, isCompleted, progressPct } = useOnboarding();

  // Only admins see the onboarding callout on the Dashboard.
  // Agents and Supervisors are not the ones doing org-level setup.
  if (user?.role !== "admin") return null;
  if (!isEligible || loading || isCompleted || !state) return null;

  const done = new Set(state.completed_steps || []);
  const skipped = new Set(state.skipped_steps || []);
  const total = steps.length - skipped.size;
  // Show the next 3 pending steps (current step plus the two after).
  const pending = steps.filter((s) => !done.has(s.id) && !skipped.has(s.id)).slice(0, 3);

  return (
    <section
      className="mt-6 border border-[var(--border)] bg-white grid grid-cols-1 lg:grid-cols-[280px_1fr]"
      data-testid="onboarding-callout"
    >
      {/* Left: progress + primary CTA */}
      <div className="p-5 border-r border-[var(--border)] bg-[var(--bg-elev)]">
        <div className="flex items-center gap-3">
          <div className="relative w-12 h-12 flex items-center justify-center">
            <svg viewBox="0 0 36 36" className="absolute w-12 h-12 -rotate-90">
              <circle cx="18" cy="18" r="15" fill="none" stroke="var(--border)" strokeWidth="3" />
              <circle
                cx="18" cy="18" r="15" fill="none"
                stroke="var(--accent)" strokeWidth="3"
                strokeDasharray={`${progressPct * 0.94} 100`}
              />
            </svg>
            <span className="text-sm font-mono font-medium" data-testid="onboarding-callout-pct">{progressPct}%</span>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">Getting started</div>
            <div className="text-sm font-medium mt-0.5">{done.size} of {total} done</div>
          </div>
        </div>
        <button
          onClick={() => navigate("/onboarding")}
          className="mt-4 w-full px-3 py-2 bg-[var(--accent)] text-white text-xs font-mono uppercase tracking-widest flex items-center justify-center gap-1.5"
          data-testid="onboarding-callout-resume-btn"
        >
          Resume setup <ArrowRight size={12} />
        </button>
      </div>

      {/* Right: next 3 pending steps */}
      <div className="p-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] px-2 py-1.5 mb-1">
          Next up
        </div>
        {pending.length === 0 ? (
          <div className="text-sm text-[var(--text-muted)] px-3 py-4 text-center">
            All caught up. <button onClick={() => navigate("/onboarding")} className="underline">Review setup</button>
          </div>
        ) : (
          pending.map((s) => (
            <button
              key={s.id}
              onClick={() => navigate(`/onboarding?step=${s.id}`)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left hover:bg-[var(--bg-elev)] transition-colors border-b border-[var(--border)] last:border-b-0"
              data-testid={`onboarding-callout-step-${s.id}`}
            >
              <Circle size={16} className="text-[var(--text-muted)] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{s.title}</div>
                <div className="text-xs text-[var(--text-muted)] truncate">{s.desc}</div>
              </div>
              <ArrowRight size={12} className="text-[var(--text-muted)] shrink-0" />
            </button>
          ))
        )}
      </div>
    </section>
  );
}
